import { useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { useAuth } from "contexts/AuthContext";

import PageHeader from "components/PageHeader";

import useAccountingReportData from "layouts/tables/data/accountingReportData";
import { EXPENSE_CATEGORIES } from "layouts/tables/data/expenseTable";
import { resolveDateRangePreset } from "utils/dateRangePresets";

const money = (value) =>
  `GHS ${Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const categoryLabel = (value) => EXPENSE_CATEGORIES.find((c) => c.value === value)?.label || value;

const SummaryCard = ({ label, value, icon, color }) => (
  <Card sx={{ p: 2.5, height: "100%" }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
      <Icon sx={{ color: `${color}.main`, fontSize: 20 }}>{icon}</Icon>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        {label}
      </Typography>
    </Box>
    <Typography variant="h4" fontWeight="bold" color={`${color}.main`}>
      {money(value)}
    </Typography>
  </Card>
);

SummaryCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  icon: PropTypes.string,
  color: PropTypes.string,
};

function AccountingReport() {
  const { church } = useAuth();
  const {
    report,
    isLoading,
    error,
    from,
    setFrom,
    to,
    setTo,
    handleApplyFilter,
    handleClearFilter,
    applyDateRange,
  } = useAccountingReportData();

  const [dateRangeType, setDateRangeType] = useState("custom");

  const handleDateRangeTypeChange = (e) => {
    const type = e.target.value;
    setDateRangeType(type);
    const range = resolveDateRangePreset(type);
    if (range) applyDateRange(range.from, range.to);
  };

  if (isLoading) {
    return <Typography sx={{ py: 6 }}>Loading accounting report...</Typography>;
  }

  if (error) {
    return (
      <Typography color="error" sx={{ py: 6 }}>
        Error: {error}
      </Typography>
    );
  }

  const totalIncome = report?.total_income ?? 0;
  const totalExpense = report?.total_expense ?? 0;
  const totalPayroll = report?.total_payroll ?? 0;
  const net = report?.net ?? 0;
  const periodLabel =
    from || to ? `${from || "Inception"} to ${to || "Date"}` : "All recorded transactions";
  const generatedAt = new Date().toLocaleString();

  return (
    <Box>
      <PageHeader
        title="Accounting Report"
        subtitle="Statement of income, expenditure and payroll."
        actions={
          <>
            <TextField
              select
              size="small"
              value={dateRangeType}
              onChange={handleDateRangeTypeChange}
              sx={{ width: 120 }}
            >
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="yearly">Yearly</MenuItem>
              <MenuItem value="custom">Custom Range</MenuItem>
            </TextField>

            {dateRangeType === "custom" && (
              <>
                <TextField
                  label="From"
                  type="date"
                  size="small"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="To"
                  type="date"
                  size="small"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <Button variant="contained" onClick={handleApplyFilter}>
                  Update
                </Button>
              </>
            )}
            <Button variant="outlined" onClick={handleClearFilter}>
              Clear
            </Button>
            <IconButton
              onClick={() => window.print()}
              sx={{ border: 1, borderColor: "divider" }}
              title="Print statement"
            >
              <Icon fontSize="small">print</Icon>
            </IconButton>
          </>
        }
      />

      {/* Statement letterhead */}
      <Card sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700}>
              {church?.name || "Church"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Statement of Income, Expenditure &amp; Payroll
            </Typography>
          </Box>
          <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Period: {periodLabel}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Generated: {generatedAt}
            </Typography>
          </Box>
        </Box>
      </Card>

      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            label="Total Church Revenue"
            value={totalIncome}
            icon="trending_up"
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            label="Total Expenses"
            value={totalExpense}
            icon="receipt_long"
            color="error"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard label="Total Payroll" value={totalPayroll} icon="groups" color="warning" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            label={net >= 0 ? "Net Surplus" : "Net Deficit"}
            value={Math.abs(net)}
            icon={net >= 0 ? "savings" : "warning_amber"}
            color={net >= 0 ? "success" : "error"}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 2.5, height: "100%" }}>
            <Typography variant="h6" mb={1.5}>
              Church Revenue by Type
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(report?.income_by_type || {}).map(([type, amount]) => (
                  <TableRow key={type}>
                    <TableCell sx={{ textTransform: "capitalize" }}>{type}</TableCell>
                    <TableCell align="right">{money(amount)}</TableCell>
                  </TableRow>
                ))}
                {(report?.group_dues_remitted_total ?? 0) > 0 && (
                  <TableRow>
                    <TableCell>Dues Remitted by Departments/Ministries</TableCell>
                    <TableCell align="right">{money(report.group_dues_remitted_total)}</TableCell>
                  </TableRow>
                )}
                {Object.keys(report?.income_by_type || {}).length === 0 &&
                  !(report?.group_dues_remitted_total > 0) && (
                    <TableRow>
                      <TableCell colSpan={2} align="center">
                        No revenue recorded for this period.
                      </TableCell>
                    </TableRow>
                  )}
              </TableBody>
              <TableBody>
                <TableRow sx={{ "& td": { borderTop: 2, borderColor: "divider" } }}>
                  <TableCell sx={{ fontWeight: 700 }}>Total Church Revenue</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {money(totalIncome)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 2.5, height: "100%" }}>
            <Typography variant="h6" mb={1.5}>
              Expenses by Category
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(report?.expense_by_category || {}).map(([category, amount]) => (
                  <TableRow key={category}>
                    <TableCell>{categoryLabel(category)}</TableCell>
                    <TableCell align="right">{money(amount)}</TableCell>
                  </TableRow>
                ))}
                {Object.keys(report?.expense_by_category || {}).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      No expenses recorded for this period.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              {Object.keys(report?.expense_by_category || {}).length > 0 && (
                <TableBody>
                  <TableRow sx={{ "& td": { borderTop: 2, borderColor: "divider" } }}>
                    <TableCell sx={{ fontWeight: 700 }}>Total Expenses</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {money(totalExpense)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 2.5, height: "100%" }}>
            <Typography variant="h6" mb={1.5}>
              Payroll Summary (paid only)
            </Typography>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>Staff paid</TableCell>
                  <TableCell align="right">{report?.payroll_count ?? 0}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Gross salary + allowances</TableCell>
                  <TableCell align="right">{money(totalPayroll)}</TableCell>
                </TableRow>
              </TableBody>
              <TableBody>
                <TableRow sx={{ "& td": { borderTop: 2, borderColor: "divider" } }}>
                  <TableCell sx={{ fontWeight: 700 }}>Total Payroll</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {money(totalPayroll)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Card>
        </Grid>
      </Grid>

      {/* Department/Ministry revenue — held by the group, not the church's
          general fund, until remitted via a monthly due. */}
      <Card sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}
        >
          <Typography variant="h6">Department &amp; Ministry Revenue (held by groups)</Typography>
          <Typography variant="h6" fontWeight={700} color="info.main">
            {money(report?.group_revenue_total)}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" mb={2}>
          Raised by departments/ministries from their own members — not part of church revenue above
          until remitted as a monthly due.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" fontWeight={700} mb={1}>
              By Department
            </Typography>
            <Table size="small">
              <TableBody>
                {(report?.group_revenue_by_department || []).map((d) => (
                  <TableRow key={d.department_id}>
                    <TableCell>{d.name}</TableCell>
                    <TableCell align="right">{money(d.total)}</TableCell>
                  </TableRow>
                ))}
                {(report?.group_revenue_by_department || []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      No department-raised revenue this period.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" fontWeight={700} mb={1}>
              By Ministry
            </Typography>
            <Table size="small">
              <TableBody>
                {(report?.group_revenue_by_ministry || []).map((m) => (
                  <TableRow key={m.ministry_id}>
                    <TableCell>{m.name}</TableCell>
                    <TableCell align="right">{money(m.total)}</TableCell>
                  </TableRow>
                ))}
                {(report?.group_revenue_by_ministry || []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      No ministry-raised revenue this period.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Grid>
        </Grid>
      </Card>

      {/* Statement summary — accounting-style derivation of the net result */}
      <Card sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" mb={1.5}>
          Statement Summary
        </Typography>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell>Total Church Revenue</TableCell>
              <TableCell align="right">{money(totalIncome)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Less: Total Expenses</TableCell>
              <TableCell align="right" sx={{ color: "error.main" }}>
                ({money(totalExpense)})
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Less: Total Payroll</TableCell>
              <TableCell align="right" sx={{ color: "error.main" }}>
                ({money(totalPayroll)})
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Divider sx={{ my: 1.5 }} />
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant="h6" fontWeight={700}>
            {net >= 0 ? "Net Surplus" : "Net Deficit"}
          </Typography>
          <Typography
            variant="h6"
            fontWeight={700}
            color={net >= 0 ? "success.main" : "error.main"}
          >
            {money(Math.abs(net))}
          </Typography>
        </Box>
      </Card>

      <Card sx={{ p: 2.5 }}>
        <Typography variant="h6" mb={1.5}>
          12-Month Trend
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Month</TableCell>
              <TableCell align="right">Church Revenue</TableCell>
              <TableCell align="right">Dept/Ministry Revenue</TableCell>
              <TableCell align="right">Expenses</TableCell>
              <TableCell align="right">Payroll</TableCell>
              <TableCell align="right">Net</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(report?.monthly || []).map((m) => (
              <TableRow key={m.month}>
                <TableCell>{m.month}</TableCell>
                <TableCell align="right">{money(m.income)}</TableCell>
                <TableCell align="right">{money(m.group_income)}</TableCell>
                <TableCell align="right">{money(m.expenses)}</TableCell>
                <TableCell align="right">{money(m.payroll)}</TableCell>
                <TableCell align="right" sx={{ color: m.net >= 0 ? "success.main" : "error.main" }}>
                  {money(m.net)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </Box>
  );
}

export default AccountingReport;
